export const LOCALES = ['ko', 'en', 'ja', 'zh', 'es', 'fr'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_LABEL: Record<Locale, string> = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
  zh: '简体中文',
  es: 'Español',
  fr: 'Français',
};

export const LOCALE_SHORT: Record<Locale, string> = {
  ko: 'KO',
  en: 'EN',
  ja: 'JP',
  zh: 'ZH',
  es: 'ES',
  fr: 'FR',
};

/** Dictionary keys — single flat namespace. Add new keys here for compile-time safety. */
export type TKey =
  | 'nav.library'
  | 'nav.myBooks'
  | 'nav.credentials'
  | 'nav.mindmap'
  | 'nav.reading'
  | 'nav.upload'
  | 'nav.achievements'
  | 'nav.admin'
  | 'nav.vault'
  | 'nav.signin'
  | 'nav.signout'
  | 'nav.localeSelect'
  | 'brand.sub'
  // Onboarding (empty library / first-time)
  | 'onboard.welcome'
  | 'onboard.welcomeSub'
  | 'onboard.featureMapTitle'
  | 'onboard.featureMapBody'
  | 'onboard.featureQuizTitle'
  | 'onboard.featureQuizBody'
  | 'onboard.featureCredTitle'
  | 'onboard.featureCredBody'
  | 'onboard.ctaBrowse'
  | 'onboard.ctaUpload'
  | 'onboard.signedOutHint'
  | 'onboard.ctaSignIn'
  | 'common.loading'
  | 'common.offline'
  | 'common.free'
  | 'common.error'
  | 'common.back'
  | 'common.continue'
  | 'common.submit'
  | 'common.skip'
  | 'common.next'
  | 'common.finish'
  | 'common.correct'
  | 'common.notQuite'
  | 'common.save'
  | 'common.beginReading'
  | 'common.startAssessment'
  | 'theme.label'
  | 'theme.light'
  | 'theme.dark'
  | 'theme.system'
  | 'sound.label'
  | 'sound.on'
  | 'sound.off'
  | 'api.error.badRequest'
  | 'api.error.unauthorized'
  | 'api.error.forbidden'
  | 'api.error.notFound'
  | 'api.error.tooLarge'
  | 'api.error.unsupported'
  | 'api.error.rateLimited'
  | 'api.error.server'
  | 'api.error.network'
  | 'api.error.unknown'
  | 'a11y.skipToMain'
  | 'notFound.kicker'
  | 'notFound.titleLine1'
  | 'notFound.titleLine2'
  | 'notFound.lead'
  | 'notFound.pathLabel'
  | 'notFound.cta'
  | 'footer.tagline'
  | 'footer.navLabel'
  | 'footer.about'
  | 'footer.privacy'
  | 'footer.terms'
  | 'footer.contact'
  | 'footer.larc'
  | 'larc.kicker'
  | 'larc.title'
  | 'larc.lead'
  | 'larc.missionLabel'
  | 'larc.missionBody'
  | 'larc.criteriaLabel'
  | 'larc.crit1'
  | 'larc.crit2'
  | 'larc.crit3'
  | 'larc.crit4'
  | 'larc.statsLabel'
  | 'larc.stApproved'
  | 'larc.stPending'
  | 'larc.stReviewed'
  | 'larc.stRejected'
  | 'larc.stTotal'
  | 'larc.stReports'
  | 'larc.note'
  | 'reader.controls'
  | 'reader.size'
  | 'reader.theme'
  | 'reader.previewFallback'
  | 'reader.stubNotice'
  | 'reader.demoBody1'
  | 'reader.demoBody2'
  | 'reader.demoBody3'
  | 'reader.demoHighlightYellow'
  | 'reader.demoHighlightCoral'
  | 'reader.demoHighlightBlue'
  | 'reader.demoHighlightMint'
  | 'reader.aside.legendLabel'
  | 'reader.aside.legendYellow'
  | 'reader.aside.legendCoral'
  | 'reader.aside.legendBlue'
  | 'reader.aside.legendMint'
  | 'reader.aside.metaLabel'
  | 'common.openMindmap'
  | 'common.searchPlaceholder'
  // Library
  | 'library.kicker'
  | 'library.heroLine1'
  | 'library.heroLine2'
  | 'library.heroSub'
  | 'library.featured'
  | 'library.curatedCount'
  | 'library.noBooks'
  | 'library.filterAll'
  | 'library.filterClassics'
  | 'library.filterPhilosophy'
  | 'library.filterK5'
  | 'library.filter68'
  | 'library.filter912'
  // Book detail
  | 'detail.previewNodes'
  | 'detail.sampledQuestions'
  | 'detail.bookMissing'
  | 'detail.noQuestions'
  | 'detail.byAuthor'
  | 'detail.pages'
  | 'detail.metaSeparator'
  | 'detail.sourceLabel'
  | 'detail.firstQuoteLabel'
  | 'detail.relatedCredentialLabel'
  | 'detail.relatedCredentialBody'
  | 'detail.propose'
  | 'detail.report'
  | 'prelearn.title'
  | 'prelearn.subtitle'
  | 'prelearn.books'
  | 'prelearn.media'
  | 'prelearn.note'
  // Mindmap
  | 'map.fourLayers'
  | 'map.navigation'
  | 'map.surface'
  | 'map.argument'
  | 'map.concept'
  | 'map.meta'
  | 'map.notReady'
  | 'map.loadingMap'
  | 'map.selectNode'
  | 'map.selectNodeSub'
  | 'map.hoverHint'
  // Quiz
  | 'quiz.of'
  | 'quiz.minRemaining'
  | 'quiz.noQuestions'
  | 'quiz.tier'
  | 'quiz.celebration.kicker'
  | 'quiz.celebration.titleLine1'
  | 'quiz.celebration.titleLine2'
  | 'quiz.celebration.lead'
  | 'quiz.celebration.correct'
  | 'quiz.celebration.theta'
  | 'quiz.celebration.viewCredential'
  | 'quiz.celebration.viewReport'
  // Credentials
  | 'creds.kicker'
  | 'creds.heroLine'
  | 'creds.heroEm'
  | 'creds.heroSub'
  | 'creds.master'
  | 'creds.recent'
  | 'creds.pastDays'
  | 'creds.noneYet'
  | 'creds.urn'
  | 'creds.issued'
  | 'creds.anchor'
  | 'creds.tier.micro'
  | 'creds.tier.standard'
  | 'creds.tier.major'
  | 'creds.tier.master'
  | 'creds.tier.legendary'
  | 'creds.count'
  | 'creds.aside.tiersLabel'
  | 'creds.aside.tiersBody'
  | 'creds.aside.verifyLabel'
  | 'creds.aside.verifyBody'
  | 'creds.aside.standardLabel'
  | 'creds.justIssued'
  | 'creds.copied'
  | 'creds.copyFailed'
  | 'creds.share'
  | 'creds.verifyExternally'
  | 'creds.verifierComing'
  | 'creds.shareNative'
  | 'creds.print'
  | 'creds.summaryLabel'
  | 'creds.summaryTotal'
  | 'creds.summaryBooks'
  | 'creds.summaryHighest'
  | 'vault.title'
  | 'vault.subtitle'
  | 'vault.kindMemo'
  | 'vault.kindQuote'
  | 'vault.kindInsight'
  | 'vault.kindFeedback'
  | 'vault.contentPlaceholder'
  | 'vault.tagsPlaceholder'
  | 'vault.add'
  | 'vault.searchPlaceholder'
  | 'vault.allKinds'
  | 'vault.empty'
  | 'vault.delete'
  | 'vault.added'
  | 'vault.deleted'
  | 'vault.needContent'
  | 'vault.export'
  | 'vault.exportMd'
  | 'vault.exportJson'
  | 'verify.title'
  | 'verify.checking'
  | 'verify.valid'
  | 'verify.invalid'
  | 'verify.notFound'
  | 'verify.tier'
  | 'verify.issued'
  | 'verify.note'
  // Source / visibility
  | 'source.curated'
  | 'source.user_upload'
  | 'source.partner'
  | 'source.public_domain'
  | 'source.institution'
  // Auth
  | 'auth.signinTitle'
  | 'auth.signupTitle'
  | 'auth.magicTitle'
  | 'auth.email'
  | 'auth.password'
  | 'auth.signin'
  | 'auth.signup'
  | 'auth.sendMagic'
  | 'auth.switchToSignup'
  | 'auth.switchToSignin'
  | 'auth.switchToMagic'
  | 'auth.notConfiguredBanner'
  | 'auth.checkEmail'
  | 'auth.magicSent'
  | 'auth.working'
  | 'auth.aside.whyLabel'
  | 'auth.aside.why1'
  | 'auth.aside.why2'
  | 'auth.aside.why3'
  | 'auth.aside.privacyLabel'
  | 'auth.aside.privacyBody'
  // Upload
  | 'upload.title'
  | 'upload.help'
  | 'upload.fileLabel'
  | 'upload.countryLabel'
  | 'upload.submit'
  | 'upload.submitting'
  | 'upload.chooseFirst'
  | 'upload.failed'
  | 'upload.resultTitle'
  | 'upload.dedupeStatus'
  | 'upload.paymentStatus'
  | 'upload.amount'
  | 'upload.jobId'
  | 'upload.eta'
  | 'upload.watchProgress'
  | 'job.title'
  | 'job.processing'
  | 'job.stage'
  | 'job.cost'
  | 'job.done'
  | 'job.failed'
  | 'job.openBook'
  | 'job.eta'
  | 'upload.aside.formatLabel'
  | 'upload.aside.formatBody'
  | 'upload.aside.etaLabel'
  | 'upload.aside.etaBody'
  | 'upload.aside.privacyLabel'
  | 'upload.aside.privacyBody'
  // Gamification (achievements)
  | 'gam.kicker'
  | 'gam.heroLine'
  | 'gam.heroEm'
  | 'gam.heroSub'
  | 'gam.levelLabel'
  | 'gam.xpTotal'
  | 'gam.xpUnit'
  | 'gam.toNext'
  | 'gam.maxLevel'
  | 'gam.badges'
  | 'gam.badgesSub'
  | 'gam.locked'
  | 'gam.noBadges'
  | 'gam.leaderboard'
  | 'gam.leaderboardSub'
  | 'gam.rank'
  | 'gam.you'
  | 'gam.emptyBoard'
  | 'gam.period.daily'
  | 'gam.period.weekly'
  | 'gam.period.monthly'
  | 'gam.period.all'
  | 'gam.howXp'
  | 'gam.howXpSub'
  | 'gam.levelCurve'
  | 'gam.levelN'
  | 'gam.aside.label'
  | 'gam.aside.body'
  | 'gam.aside.fairLabel'
  | 'gam.aside.fairBody'
  | 'gam.xpRule.answer_correct'
  | 'gam.xpRule.stage_complete'
  | 'gam.xpRule.book_clear'
  | 'gam.xpRule.cr_submit'
  | 'gam.xpRule.streak_day'
  | 'gam.xpRule.question_propose'
  | 'gam.xpRule.question_adopted'
  | 'gam.xpRule.review_participate'
  // Achievement report (학습자 성취 리포트)
  | 'report.kicker'
  | 'report.title'
  | 'report.accuracy'
  | 'report.level'
  | 'report.theta'
  | 'report.thetaNote'
  | 'report.attempted'
  | 'report.unit'
  | 'report.naep'
  | 'report.lidia'
  | 'report.trend'
  | 'report.window'
  | 'report.notAttempted'
  | 'report.empty'
  | 'report.emptySub'
  | 'report.startQuiz'
  | 'report.honestyLabel'
  | 'report.honestyBody'
  | 'report.viewCredential'
  | 'report.viewAchievements'
  | 'report.retry'
  | 'report.naep.locate_recall'
  | 'report.naep.integrate_interpret'
  | 'report.naep.critique_evaluate'
  // Question proposals (사용자 문제 제안)
  | 'prop.title'
  | 'prop.help'
  | 'prop.stemLabel'
  | 'prop.stemPlaceholder'
  | 'prop.choicesLabel'
  | 'prop.choiceCorrect'
  | 'prop.choicePlaceholder'
  | 'prop.explanationLabel'
  | 'prop.explanationPlaceholder'
  | 'prop.submit'
  | 'prop.submitting'
  | 'prop.success'
  | 'prop.needStem'
  | 'prop.needChoices'
  | 'prop.needCorrect'
  | 'prop.myProposals'
  | 'prop.none'
  | 'prop.status.pending'
  | 'prop.status.accepted'
  | 'prop.status.rejected'
  | 'prop.aside.howLabel'
  | 'prop.aside.howBody'
  | 'prop.aside.rewardLabel'
  | 'prop.aside.rewardBody'
  | 'prop.difficulty'
  | 'prop.difficultyNone';
